/**
 * Transfer Page Component
 *
 * Handles DTA → Web session transfer (Flow 3b).
 * This page is opened in the browser when the DTA provides a transfer URL.
 *
 * Flow:
 * 1. Extract token from URL query parameter
 * 2. Redirect to backend /auth/transfer endpoint with the token
 * 3. Backend validates/consumes token and creates session
 * 4. Backend redirects to dashboard on success (with ?transferred=true), or login on error
 * 5. DashboardLayout detects ?transferred=true and broadcasts to other tabs
 *
 * Note: This page is mostly a loading/redirect page since the backend
 * handles the actual session creation via redirect.
 */
// deploy-test: verifying Vercel auto-deploy with valid commit author email

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import usePageTitle from '../../hooks/usePageTitle';

const TransferPage = () => {
  usePageTitle('Signing you in');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');
  const redirect = searchParams.get('redirect');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Missing transfer token. Please try again from the desktop app.');
      return;
    }

    // NOTE: BroadcastChannel notification moved to DashboardLayout.js
    // The session doesn't exist until after the backend redirect completes,
    // so we broadcast from DashboardLayout when ?transferred=true is detected.

    // Redirect to backend transfer endpoint immediately
    const redirectParam = redirect ? `&redirect=${encodeURIComponent(redirect)}` : '';
    window.location.href = `${API_BASE_URL}/auth/transfer?token=${encodeURIComponent(token)}${redirectParam}`;
  }, [token, redirect]);

  // Error state UI
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Transfer Failed
              </h1>
              <p className="text-gray-600">
                {errorMessage}
              </p>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard/login')}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Sign in manually
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Go to homepage
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Blank white screen while redirecting
  return <div className="min-h-screen bg-white" />;
};

export default TransferPage;
